import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ComplianceRequirements} from '@gatewee/types/domain-setting';

@Component({
  selector: 'app-documents-form',
  templateUrl: './documents-form.component.html',
  styleUrls: ['./documents-form.component.css'],
  standalone: false,
})
export class DocumentsFormComponent implements OnChanges, OnInit {
  constructor(private readonly fb: FormBuilder) {
    this.updateDocumentEntries();
  }

  ngOnInit(): void {
    this.syncRequiredDocumentControls();
  }

  @Input() formGroup!: FormArray;

  @Input() requiredDocuments: ComplianceRequirements = {};

  documentEntries: Array<{key: string; value: boolean}> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requiredDocuments'] || changes['formGroup']) {
      this.updateDocumentEntries();
      this.syncRequiredDocumentControls();
    }
  }

  private updateDocumentEntries(): void {
    this.documentEntries = Object.entries(this.requiredDocuments || {})
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value)
      .map(([key, value]) => ({
        key,
        value: value ?? false,
      }));
  }

  syncRequiredDocumentControls(): void {
    if (!this.formGroup) {
      return;
    }

    for (const entry of this.documentEntries) {
      if (!entry.value) {
        continue;
      }

      const index = this.formGroup.controls.findIndex(
        control => control.get('type')?.value === entry.key,
      );

      if (index === -1) {
        this.formGroup.push(
          this.fb.group({
            type: [entry.key, Validators.required],
            base64: [null, Validators.required],
          }),
        );
      } else {
        const base64Control = this.formGroup.at(index).get('base64');
        if (!base64Control?.value) {
          base64Control?.setValidators([Validators.required]);
          base64Control?.updateValueAndValidity();
        }
      }
    }

    this.formGroup.updateValueAndValidity();
  }

  isDocumentUploaded(key: string): boolean {
    const control = this.formGroup.controls.find(
      c => c.get('type')?.value === key,
    );
    return !!control?.get('base64')?.value;
  }

  requiredUploadProgress(): {uploaded: number; total: number} {
    const total = this.documentEntries.filter(e => e.value).length;
    const uploaded = this.documentEntries.filter(
      e => e.value && this.isDocumentUploaded(e.key),
    ).length;
    return {uploaded, total};
  }

  onFileChange(file: File | undefined, field: string): void {
    const isRequired =
      this.requiredDocuments[field as keyof ComplianceRequirements] ?? false;

    if (!file) {
      const index = this.formGroup.controls.findIndex(
        (control: unknown) =>
          (control as FormGroup).get('type')?.value === field,
      );

      if (index !== -1) {
        const formControl = this.formGroup.at(index).get('base64');
        formControl?.setValue(null);
        if (isRequired) {
          formControl?.setValidators([Validators.required]);
          formControl?.setErrors({required: true});
          formControl?.markAsTouched();
        }
        formControl?.updateValueAndValidity();
        this.formGroup.updateValueAndValidity();
      }
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const base64String = reader.result as string;

      const index = this.formGroup.controls.findIndex(
        (control: unknown) =>
          (control as FormGroup).get('type')?.value === field,
      );

      if (index !== -1) {
        const formControl = this.formGroup.at(index).get('base64');
        formControl?.setValue(base64String);
        if (isRequired) {
          formControl?.setValidators([Validators.required]);
        } else {
          formControl?.clearValidators();
        }
        formControl?.updateValueAndValidity();
      } else {
        const validators = isRequired ? [Validators.required] : [];
        const newFormGroup = this.fb.group({
          type: [field, Validators.required],
          base64: [base64String, validators],
        });
        this.formGroup.push(newFormGroup);
      }

      this.formGroup.updateValueAndValidity();
    };

    reader.onerror = error => {
      console.error('Erro ao converter para base64:', error);
    };

    reader.readAsDataURL(file);
  }
}
